import { GlowFilter } from "pixi-filters";
import { Container, Ellipse, Graphics } from "pixi.js";

interface PlayheadProps {
  width: number;
  height: number;
  /** Distance in pixels from the bottom edge (= top of piano keyboard) */
  distanceFromBottom: number;
  /** Optional color override (defaults to PLAYHEAD_DEFAULT_COLOR) */
  color?: number;
}

const PLAYHEAD_DEFAULT_COLOR = 0x8b5cf6;
const PLAYHEAD_WIDTH = 1;

/**
 * Draws the "instant T" horizontal bar on the piano roll.
 *
 * Rendered as a solid 2px line (color #8b5cf6) with a custom GlowFilter
 * producing a neon halo effect, same visual language as the FloatingLines
 * wave shader (intensity / (|dist| + falloff)).
 *
 * Positioned at:
 *   y = height - distanceFromBottom
 *
 * Skips redraw if props haven't changed (same pattern as Background).
 */
export class Playhead {
  private line: Graphics;
  private previousProps?: PlayheadProps;

  constructor(private stage: Container) {
    this.line = new Graphics();
    this.line.filters = [
      new GlowFilter({
        color: 0x2f4bc0,
        alpha: 0.5,
        // color: PLAYHEAD_COLOR,
        distance: 18,
        innerStrength: 0,
        outerStrength: 6,
      }),
    ];

    this.stage.addChild(this.line);
  }

  destroy(): void {
    this.line.destroy();
  }

  draw(props: PlayheadProps): void {
    if (!this.shouldRedraw(props)) {
      return;
    }

    const y = props.height - props.distanceFromBottom;
    const color = props.color ?? PLAYHEAD_DEFAULT_COLOR;

    // Update glow filter color to match
    const glow = this.line.filters?.[0] as GlowFilter | undefined;
    if (glow) {
      glow.color = color;
    }

    this.line
      .clear()
      .moveTo(0, y)
      .lineTo(props.width, y)
      .stroke({ color, alpha: 1, width: PLAYHEAD_WIDTH });
  }

  private shouldRedraw(props: PlayheadProps): boolean {
    if (!this.previousProps) {
      this.previousProps = { ...props };
      return true;
    }

    const hasChanged =
      this.previousProps.width !== props.width ||
      this.previousProps.height !== props.height ||
      this.previousProps.distanceFromBottom !== props.distanceFromBottom ||
      this.previousProps.color !== props.color;

    this.previousProps = { ...props };

    return hasChanged;
  }
}
